import React, { useState, useEffect, useContext } from "react";
import CollapseHeader from "../../../core/common/collapse-header";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import FieldConfigurationSidebar from "../../../components/Tabs/FieldConfigurationSidebar";
import { AuthContext } from "../../../context/authcontext";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import {
  SalesActivityService,
  SalesActivityType,
  SalesActivityTypeStatus,
  toSalesActivityKey,
} from "../../crm/salesActivity/salesActivity.service";
import {
  showSuccessAlert,
  showErrorAlert,
  showDeleteConfirmationAlert,
} from "../../../utils/alertUtils";
import IconPicker from "../../../components/IconPicker/IconPicker";
import {
  DEFAULT_ACTIVITY_ICON,
  resolveActivityIcon,
} from "../../../components/IconPicker/activityIcons";
import "./SalesActivityTypeSettings.css";

type EditTarget =
  | { kind: "type"; id: number }
  | { kind: "status"; id: number }
  | null;

const SalesActivityTypeSettings = () => {
  const { selectedTenantId, auth } = useContext(AuthContext);
  const tenantId = Number(selectedTenantId);
  const userRole = useTenantRole(selectedTenantId || 0);
  const isSalesUser = userRole?.role_key === "SALESUSER";

  const [activityTypes, setActivityTypes] = useState<SalesActivityType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [defaultExpanded, setDefaultExpanded] = useState<boolean>(() => {
    return localStorage.getItem("satDefaultExpanded") === "true";
  });
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setExpandedSections(prev => {
      const newExpanded = { ...prev };
      let changed = false;
      activityTypes.forEach(t => {
        if (newExpanded[t.crm_sales_activity_type_id] === undefined) {
          newExpanded[t.crm_sales_activity_type_id] = defaultExpanded;
          changed = true;
        }
      });
      return changed ? newExpanded : prev;
    });
  }, [activityTypes, defaultExpanded]);

  const handleToggleDefaultExpanded = () => {
    const newVal = !defaultExpanded;
    setDefaultExpanded(newVal);
    localStorage.setItem("satDefaultExpanded", String(newVal));
    setExpandedSections(prev => {
      const newExpanded = { ...prev };
      activityTypes.forEach(t => {
        newExpanded[t.crm_sales_activity_type_id] = newVal;
      });
      return newExpanded;
    });
  };

  const toggleSection = (id: number) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const [editing, setEditing] = useState<EditTarget>(null);
  const [editingValue, setEditingValue] = useState("");

  const [addingStatusForTypeId, setAddingStatusForTypeId] = useState<
    number | null
  >(null);
  const [newStatusName, setNewStatusName] = useState("");
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);

  const [isAddingType, setIsAddingType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeIcon, setNewTypeIcon] = useState(DEFAULT_ACTIVITY_ICON);
  const [isSubmittingType, setIsSubmittingType] = useState(false);
  const [savingIconForTypeId, setSavingIconForTypeId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!selectedTenantId) return;
    fetchData();
  }, [selectedTenantId]);

  const fetchData = async (showLoading: boolean = true) => {
    if (showLoading) setIsLoading(true);
    try {
      const types = await SalesActivityService.getActivityTypes(tenantId);
      setActivityTypes(types);
    } catch (error) {
      console.error("Failed to fetch sales activity types:", error);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const startEditType = (type: SalesActivityType) => {
    if (isSalesUser) return;
    setEditing({ kind: "type", id: type.crm_sales_activity_type_id });
    setEditingValue(type.crm_sales_activity_type_display_name);
  };

  const startEditStatus = (status: SalesActivityTypeStatus) => {
    if (isSalesUser) return;
    setEditing({
      kind: "status",
      id: status.crm_sales_activity_type_status_id,
    });
    setEditingValue(status.crm_sales_activity_type_status_display_name);
  };

  const cancelEditing = () => {
    setEditing(null);
    setEditingValue("");
  };

  const commitEditType = async (type: SalesActivityType) => {
    const value = editingValue.trim();
    if (!value || value === type.crm_sales_activity_type_display_name) {
      cancelEditing();
      return;
    }
    try {
      await SalesActivityService.updateActivityType(
        type.crm_sales_activity_type_id,
        {
          crm_sales_activity_type_display_name: value,
          crm_sales_activity_type_key: toSalesActivityKey(value),
          tenant_id: tenantId,
          updated_by: auth?.user_id,
        }
      );
      await fetchData(false);
    } catch (error) {
      console.error("Failed to update activity type:", error);
      showErrorAlert("Failed to update activity type.");
    } finally {
      cancelEditing();
    }
  };

  const commitEditStatus = async (status: SalesActivityTypeStatus) => {
    const value = editingValue.trim();
    if (
      !value ||
      value === status.crm_sales_activity_type_status_display_name
    ) {
      cancelEditing();
      return;
    }
    try {
      await SalesActivityService.updateActivityTypeStatus(
        status.crm_sales_activity_type_status_id,
        {
          crm_sales_activity_type_status_display_name: value,
          crm_sales_activity_type_status_key: toSalesActivityKey(value),
          tenant_id: tenantId,
          updated_by: auth?.user_id,
        }
      );
      await fetchData(false);
    } catch (error) {
      console.error("Failed to update activity status:", error);
      showErrorAlert("Failed to update status.");
    } finally {
      cancelEditing();
    }
  };

  const handleDeleteType = async (type: SalesActivityType) => {
    const result = await showDeleteConfirmationAlert(
      "Delete this activity type?",
      `"${type.crm_sales_activity_type_display_name}" and all its statuses will be removed.`
    );
    if (!result.isConfirmed) return;

    try {
      await SalesActivityService.deleteActivityType(
        type.crm_sales_activity_type_id
      );
      showSuccessAlert("Activity type deleted successfully.");
      await fetchData(false);
    } catch (error) {
      console.error("Failed to delete activity type:", error);
      showErrorAlert("Failed to delete activity type.");
    }
  };

  const handleDeleteStatus = async (status: SalesActivityTypeStatus) => {
    const result = await showDeleteConfirmationAlert(
      "Delete this status?",
      `"${status.crm_sales_activity_type_status_display_name}" will be removed.`
    );
    if (!result.isConfirmed) return;

    try {
      await SalesActivityService.deleteActivityTypeStatus(
        status.crm_sales_activity_type_status_id
      );
      showSuccessAlert("Status deleted successfully.");
      await fetchData(false);
    } catch (error) {
      console.error("Failed to delete status:", error);
      showErrorAlert("Failed to delete status.");
    }
  };

  const startAddStatus = (typeId: number) => {
    if (isSalesUser) return;
    setAddingStatusForTypeId(typeId);
    setNewStatusName("");
  };

  const cancelAddStatus = () => {
    setAddingStatusForTypeId(null);
    setNewStatusName("");
  };

  const commitAddStatus = async (typeId: number) => {
    if (isSubmittingStatus) return;
    const value = newStatusName.trim();
    if (!value) {
      cancelAddStatus();
      return;
    }
    setIsSubmittingStatus(true);
    try {
      await SalesActivityService.createActivityTypeStatus({
        crm_sales_activity_type_status_key: toSalesActivityKey(value),
        crm_sales_activity_type_status_display_name: value,
        crm_sales_activity_type_id: typeId,
        tenant_id: tenantId,
        is_active: true,
        created_by: auth?.user_id,
        updated_by: auth?.user_id,
      });
      await fetchData(false);
    } catch (error) {
      console.error("Failed to add status:", error);
      showErrorAlert("Failed to add status.");
    } finally {
      setIsSubmittingStatus(false);
      cancelAddStatus();
    }
  };

  const startAddType = () => {
    if (isSalesUser) return;
    setIsAddingType(true);
    setNewTypeName("");
    setNewTypeIcon(DEFAULT_ACTIVITY_ICON);
  };

  const cancelAddType = () => {
    setIsAddingType(false);
    setNewTypeName("");
    setNewTypeIcon(DEFAULT_ACTIVITY_ICON);
  };

  const commitAddType = async () => {
    if (isSubmittingType) return;
    const value = newTypeName.trim();
    if (!value) {
      cancelAddType();
      return;
    }
    setIsSubmittingType(true);
    try {
      await SalesActivityService.createActivityType({
        crm_sales_activity_type_key: toSalesActivityKey(value),
        crm_sales_activity_type_display_name: value,
        icon: newTypeIcon,
        tenant_id: tenantId,
        is_active: true,
        created_by: auth?.user_id,
        updated_by: auth?.user_id,
      });
      await fetchData(false);
    } catch (error) {
      console.error("Failed to add activity type:", error);
      showErrorAlert("Failed to add activity type.");
    } finally {
      setIsSubmittingType(false);
      cancelAddType();
    }
  };

  const commitChangeTypeIcon = async (type: SalesActivityType, icon: string) => {
    if (icon === resolveActivityIcon(type.icon, type.crm_sales_activity_type_display_name)) {
      return;
    }
    setSavingIconForTypeId(type.crm_sales_activity_type_id);
    try {
      await SalesActivityService.updateActivityType(
        type.crm_sales_activity_type_id,
        {
          icon,
          tenant_id: tenantId,
          updated_by: auth?.user_id,
        }
      );
      await fetchData(false);
    } catch (error) {
      console.error("Failed to update activity type icon:", error);
      showErrorAlert("Failed to update icon.");
    } finally {
      setSavingIconForTypeId(null);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <SettingsTabsNavigation
                  activeTab="field-configuration"
                  variant="bottom"
                  includeIcons={true}
                  context="organization"
                />
              </div>
            </div>

            <div className="row">
              <FieldConfigurationSidebar />

              <div className="col-xl-9 col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-3">
                      <h4 className="fw-semibold mb-1">
                        Activity Types
                      </h4>
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <p className="mb-0">
                          Configure the activity types and their
                          statuses below
                        </p>
                        <div
                          className="sat-toggle-container"
                          onClick={handleToggleDefaultExpanded}
                        >
                          <span>Expand All</span>
                          <div className={`sat-toggle-switch ${defaultExpanded ? 'active' : ''}`} />
                        </div>
                      </div>
                    </div>

                    {isLoading ? (
                      <div className="text-center py-4">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {activityTypes.length === 0 && !isAddingType && (
                          <p className="text-muted mb-3">
                            No activity types yet. Add one to get started.
                          </p>
                        )}

                        {activityTypes.map((type) => {
                          const isExpanded = expandedSections[type.crm_sales_activity_type_id];

                          return (
                            <div
                              className="sat-block"
                              key={type.crm_sales_activity_type_id}
                            >
                              <div
                                className={`sat-heading-row ${isExpanded ? '' : 'collapsed'}`}
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  if ((e.target as HTMLElement).closest('.sat-heading, .sat-new-type-input, .sat-heading-icon, .sat-delete-type-btn')) {
                                    return;
                                  }
                                  toggleSection(type.crm_sales_activity_type_id);
                                }}
                              >
                                <div className="sat-heading-left">
                                  <div className={`sat-chevron ${isExpanded ? 'expanded' : ''}`}>
                                    <i className="ti ti-chevron-right" />
                                  </div>
                                  {(() => {
                                    const isEditingThisType =
                                      editing?.kind === "type" &&
                                      editing.id ===
                                      type.crm_sales_activity_type_id;
                                    return (
                                      <>
                                        <IconPicker
                                          toggleClassName={
                                            isEditingThisType
                                              ? undefined
                                              : "sat-heading-icon"
                                          }
                                          value={resolveActivityIcon(
                                            type.icon,
                                            type.crm_sales_activity_type_display_name
                                          )}
                                          disabled={
                                            savingIconForTypeId ===
                                            type.crm_sales_activity_type_id ||
                                            isSalesUser
                                          }
                                          onChange={(icon) =>
                                            commitChangeTypeIcon(type, icon)
                                          }
                                        />
                                        {isEditingThisType ? (
                                          <input
                                            autoFocus
                                            className="sat-new-type-input"
                                            value={editingValue}
                                            onChange={(e) =>
                                              setEditingValue(e.target.value)
                                            }
                                            onBlur={() => commitEditType(type)}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter")
                                                e.currentTarget.blur();
                                              if (e.key === "Escape")
                                                cancelEditing();
                                            }}
                                          />
                                        ) : (
                                          <h2
                                            className="sat-heading"
                                            onClick={() => startEditType(type)}
                                            title="Click to rename"
                                          >
                                            {
                                              type.crm_sales_activity_type_display_name
                                            }
                                          </h2>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                {!isSalesUser && (
                                  <button
                                    type="button"
                                    className="sat-delete-type-btn"
                                    onClick={() => handleDeleteType(type)}
                                    title="Delete activity type"
                                  >
                                    <i className="ti ti-trash" />
                                  </button>
                                )}
                              </div>

                              <div className={`sat-tree-wrapper ${isExpanded ? 'expanded' : ''}`}>
                                <div className="sat-tree-inner">
                                  <div className="sat-tree">
                                    {type.statuses.map((status) => (
                                      <div
                                        className="sat-branch"
                                        key={
                                          status.crm_sales_activity_type_status_id
                                        }
                                      >
                                        <div className="sat-status-row">
                                          {editing?.kind === "status" &&
                                            editing.id ===
                                            status.crm_sales_activity_type_status_id ? (
                                            <input
                                              autoFocus
                                              className="sat-status-input"
                                              value={editingValue}
                                              onChange={(e) =>
                                                setEditingValue(e.target.value)
                                              }
                                              onBlur={() =>
                                                commitEditStatus(status)
                                              }
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter")
                                                  e.currentTarget.blur();
                                                if (e.key === "Escape")
                                                  cancelEditing();
                                              }}
                                            />
                                          ) : (
                                            <span
                                              className="sat-status-name"
                                              onClick={() => startEditStatus(status)}
                                              title="Click to rename"
                                            >
                                              {
                                                status.crm_sales_activity_type_status_display_name
                                              }
                                            </span>
                                          )}
                                          {!isSalesUser && (
                                            <button
                                              type="button"
                                              className="sat-status-delete-btn"
                                              onClick={() =>
                                                handleDeleteStatus(status)
                                              }
                                              title="Delete status"
                                            >
                                              <i className="ti ti-trash" />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}

                                    {!isSalesUser && (
                                      <div className="sat-branch">
                                        {addingStatusForTypeId ===
                                          type.crm_sales_activity_type_id ? (
                                          <div className="sat-status-row">
                                            <input
                                              autoFocus
                                              className="sat-status-input"
                                              placeholder="New status name"
                                              value={newStatusName}
                                              disabled={isSubmittingStatus}
                                              onChange={(e) =>
                                                setNewStatusName(e.target.value)
                                              }
                                              onBlur={() =>
                                                commitAddStatus(
                                                  type.crm_sales_activity_type_id
                                                )
                                              }
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter")
                                                  e.currentTarget.blur();
                                                if (e.key === "Escape")
                                                  cancelAddStatus();
                                              }}
                                            />
                                            {isSubmittingStatus && (
                                              <div className="sat-inline-spinner" />
                                            )}
                                          </div>
                                        ) : (
                                          <button
                                            type="button"
                                            className="sat-add-status-node"
                                            onClick={() =>
                                              startAddStatus(
                                                type.crm_sales_activity_type_id
                                              )
                                            }
                                          >
                                            <i className="fa fa-plus" /> Add Status
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {isAddingType ? (
                          <div
                            className="d-inline-flex align-items-center gap-2"
                            onBlur={(e) => {
                              // Picking an icon focuses a button inside this
                              // same row — only commit once focus leaves the
                              // whole row (name + icon picker together).
                              if (
                                !e.currentTarget.contains(
                                  e.relatedTarget as Node
                                )
                              ) {
                                commitAddType();
                              }
                            }}
                          >
                            <IconPicker
                              value={newTypeIcon}
                              onChange={setNewTypeIcon}
                              disabled={isSubmittingType}
                            />
                            <input
                              autoFocus
                              className="sat-new-type-input"
                              placeholder="New activity type name"
                              value={newTypeName}
                              disabled={isSubmittingType}
                              onChange={(e) => setNewTypeName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  e.currentTarget.blur();
                                if (e.key === "Escape") cancelAddType();
                              }}
                            />
                            {isSubmittingType && (
                              <div className="sat-inline-spinner" />
                            )}
                          </div>
                        ) : (
                          !isSalesUser && (
                            <button
                              type="button"
                              className="sat-add-type-btn"
                              onClick={startAddType}
                            >
                              <i className="fa fa-plus" /> Add Activity Type
                            </button>
                          )
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesActivityTypeSettings;
